import type { Dispatcher } from '../dispatcher.js';
import { ToolError } from '../dispatcher.js';
import { ERROR_CODES } from '../wsClient.js';
import { serializeSession } from '../serializer.js';
import {
  nowIso,
  store,
  getStudioContext,
  type PlaceholderTrack,
  type StudioContextLike,
} from './store.js';

interface ListedTrack {
  id: string;
  name: string;
  type: PlaceholderTrack['type'];
  color: string;
  volume_db: number;
  pan: number;
  mute: boolean;
  solo: boolean;
}

interface ListTracksResult {
  tracks: ListedTrack[];
}

interface SetTrackVolumeParams {
  track_id: string;
  db: number;
}
interface SetTrackVolumeResult {
  track_id: string;
  volume_db: number;
  applied_at: string;
}

interface SetTrackMuteParams {
  track_id: string;
  mute: boolean;
}
interface SetTrackMuteResult {
  track_id: string;
  mute: boolean;
  applied_at: string;
}

interface SetTrackSoloParams {
  track_id: string;
  solo: boolean;
}
interface SetTrackSoloResult {
  track_id: string;
  solo: boolean;
  applied_at: string;
}

interface SetTrackPanParams {
  track_id: string;
  pan: number;
}
interface SetTrackPanResult {
  track_id: string;
  pan: number;
  applied_at: string;
}

interface SetTrackNameParams {
  track_id: string;
  name: string;
}
interface SetTrackNameResult {
  track_id: string;
  name: string;
  applied_at: string;
}

// --- Read path ----------------------------------------------------------
// `listTracks` always reflects the *current* project as seen by the
// `StudioContextLike.getProject()` snapshot when a studio is attached. We
// route through `serializeSession` so that whatever shape openDAW hands us
// (a real `Project`, a snapshot dict, or `null`) gets translated into the
// canonical §5.1 Track shape that the agent's tool schemas expect.
function listTracks(): ListTracksResult {
  const ctx = getStudioContext();
  if (ctx === null) {
    return {
      tracks: store.getProject().tracks.map(toListedTrack),
    };
  }
  const session = serializeSession(ctx.getProject());
  return {
    tracks: session.tracks.map((t) => ({
      id: t.id,
      name: t.name,
      type: t.type,
      color: t.color,
      volume_db: t.volume_db,
      pan: t.pan,
      mute: t.mute,
      solo: t.solo,
    })),
  };
}

function toListedTrack(t: PlaceholderTrack): ListedTrack {
  return {
    id: t.id,
    name: t.name,
    type: t.type,
    color: t.color,
    volume_db: t.volume_db,
    pan: t.pan,
    mute: t.mute,
    solo: t.solo,
  };
}

// --- Helpers ------------------------------------------------------------
// When a studio context is attached but we cannot find the track by id in
// its snapshot, that's a 4xx-style user error: the agent referenced a track
// that doesn't exist in the live project.
function findStudioTrack(ctx: StudioContextLike, track_id: string): ListedTrack | undefined {
  const session = serializeSession(ctx.getProject());
  const match = session.tracks.find((t) => t.id === track_id);
  if (match === undefined) return undefined;
  return {
    id: match.id,
    name: match.name,
    type: match.type,
    color: match.color,
    volume_db: match.volume_db,
    pan: match.pan,
    mute: match.mute,
    solo: match.solo,
  };
}

function trackNotFound(track_id: string): ToolError {
  return new ToolError(ERROR_CODES.INVALID_PARAMS, `Track ${track_id} does not exist`, {
    code: 'TRACK_NOT_FOUND',
    track_id,
  });
}

// --- Write path ---------------------------------------------------------
// Each mutator follows the same template:
//   1. Validate params (throws on bad input).
//   2. If a studio context is attached and exposes the matching mutator,
//      delegate to it (this is the real openDAW write path via
//      `editing.modify(...)` inside the adapter).
//   3. Re-read the current value from the live snapshot for the response.
//   4. If no studio is attached, fall back to the placeholder store so the
//      agent still receives a coherent reply during bring-up.

function setTrackVolume(params: SetTrackVolumeParams): SetTrackVolumeResult {
  if (!params || typeof params.track_id !== 'string' || typeof params.db !== 'number') {
    throw new ToolError(ERROR_CODES.INVALID_PARAMS, 'setTrackVolume: track_id, db required');
  }
  if (!Number.isFinite(params.db) || params.db < -120 || params.db > 12) {
    throw new ToolError(ERROR_CODES.INVALID_PARAMS, 'db must be a finite number in [-120, 12]');
  }
  const ctx = getStudioContext();
  if (ctx !== null) {
    const live = findStudioTrack(ctx, params.track_id);
    if (live === undefined) throw trackNotFound(params.track_id);
    if (typeof ctx.setTrackVolume === 'function') {
      // Routes through `editing.modify(() => audioUnit.namedParameter.volume.setValue(db))`
      // in the adapter (see openDawAdapter.makeStudioContext).
      ctx.setTrackVolume(params.track_id, params.db);
      const after = findStudioTrack(ctx, params.track_id) ?? live;
      return { track_id: after.id, volume_db: after.volume_db, applied_at: nowIso() };
    }
    return { track_id: live.id, volume_db: live.volume_db, applied_at: nowIso() };
  }
  const track = requirePlaceholderTrack(params.track_id);
  track.volume_db = params.db;
  return { track_id: track.id, volume_db: track.volume_db, applied_at: nowIso() };
}

function setTrackMute(params: SetTrackMuteParams): SetTrackMuteResult {
  if (!params || typeof params.track_id !== 'string' || typeof params.mute !== 'boolean') {
    throw new ToolError(ERROR_CODES.INVALID_PARAMS, 'setTrackMute: track_id, mute required');
  }
  const ctx = getStudioContext();
  if (ctx !== null) {
    const live = findStudioTrack(ctx, params.track_id);
    if (live === undefined) throw trackNotFound(params.track_id);
    if (typeof ctx.setTrackMute === 'function') {
      ctx.setTrackMute(params.track_id, params.mute);
      const after = findStudioTrack(ctx, params.track_id) ?? live;
      return { track_id: after.id, mute: after.mute, applied_at: nowIso() };
    }
    return { track_id: live.id, mute: live.mute, applied_at: nowIso() };
  }
  const track = requirePlaceholderTrack(params.track_id);
  track.mute = params.mute;
  return { track_id: track.id, mute: track.mute, applied_at: nowIso() };
}

function setTrackSolo(params: SetTrackSoloParams): SetTrackSoloResult {
  if (!params || typeof params.track_id !== 'string' || typeof params.solo !== 'boolean') {
    throw new ToolError(ERROR_CODES.INVALID_PARAMS, 'setTrackSolo: track_id, solo required');
  }
  const ctx = getStudioContext();
  if (ctx !== null) {
    const live = findStudioTrack(ctx, params.track_id);
    if (live === undefined) throw trackNotFound(params.track_id);
    if (typeof ctx.setTrackSolo === 'function') {
      ctx.setTrackSolo(params.track_id, params.solo);
      const after = findStudioTrack(ctx, params.track_id) ?? live;
      return { track_id: after.id, solo: after.solo, applied_at: nowIso() };
    }
    return { track_id: live.id, solo: live.solo, applied_at: nowIso() };
  }
  const track = requirePlaceholderTrack(params.track_id);
  track.solo = params.solo;
  return { track_id: track.id, solo: track.solo, applied_at: nowIso() };
}

function setTrackPan(params: SetTrackPanParams): SetTrackPanResult {
  if (!params || typeof params.track_id !== 'string' || typeof params.pan !== 'number') {
    throw new ToolError(ERROR_CODES.INVALID_PARAMS, 'setTrackPan: track_id, pan required');
  }
  if (!Number.isFinite(params.pan) || params.pan < -1 || params.pan > 1) {
    throw new ToolError(ERROR_CODES.INVALID_PARAMS, 'pan must be a finite number in [-1, 1]');
  }
  const ctx = getStudioContext();
  if (ctx !== null) {
    const live = findStudioTrack(ctx, params.track_id);
    if (live === undefined) throw trackNotFound(params.track_id);
    if (typeof ctx.setTrackPan === 'function') {
      // openDAW's `panning` parameter is bipolar [-1, 1] via `ValueMapping.bipolar()`.
      ctx.setTrackPan(params.track_id, params.pan);
      const after = findStudioTrack(ctx, params.track_id) ?? live;
      return { track_id: after.id, pan: after.pan, applied_at: nowIso() };
    }
    return { track_id: live.id, pan: live.pan, applied_at: nowIso() };
  }
  const track = requirePlaceholderTrack(params.track_id);
  track.pan = params.pan;
  return { track_id: track.id, pan: track.pan, applied_at: nowIso() };
}

function setTrackName(params: SetTrackNameParams): SetTrackNameResult {
  if (!params || typeof params.track_id !== 'string' || typeof params.name !== 'string') {
    throw new ToolError(ERROR_CODES.INVALID_PARAMS, 'setTrackName: track_id, name required');
  }
  const ctx = getStudioContext();
  if (ctx !== null) {
    const live = findStudioTrack(ctx, params.track_id);
    if (live === undefined) throw trackNotFound(params.track_id);
    if (typeof ctx.setTrackName === 'function') {
      // Track names live on the input-device label field
      // (see TrackBoxAdapter#catchupAndSubscribeTargetName).
      ctx.setTrackName(params.track_id, params.name);
      const after = findStudioTrack(ctx, params.track_id) ?? live;
      return { track_id: after.id, name: after.name, applied_at: nowIso() };
    }
    return { track_id: live.id, name: live.name, applied_at: nowIso() };
  }
  const track = requirePlaceholderTrack(params.track_id);
  track.name = params.name;
  return { track_id: track.id, name: track.name, applied_at: nowIso() };
}

function requirePlaceholderTrack(track_id: string): PlaceholderTrack {
  const t = store.getTrack(track_id);
  if (!t) throw trackNotFound(track_id);
  return t;
}

export const trackHandlers = {
  listTracks,
  setTrackVolume,
  setTrackMute,
  setTrackSolo,
  setTrackPan,
  setTrackName,
} as const;

export function registerTrackHandlers(d: Dispatcher): void {
  d.register('listTracks', listTracks);
  d.register<SetTrackVolumeParams, SetTrackVolumeResult>('setTrackVolume', setTrackVolume);
  d.register<SetTrackMuteParams, SetTrackMuteResult>('setTrackMute', setTrackMute);
  d.register<SetTrackSoloParams, SetTrackSoloResult>('setTrackSolo', setTrackSolo);
  d.register<SetTrackPanParams, SetTrackPanResult>('setTrackPan', setTrackPan);
  d.register<SetTrackNameParams, SetTrackNameResult>('setTrackName', setTrackName);
}
